import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    reportService,
    IReportRequest,
    PM_REPORT_FREQUENCY_OPTIONS,
    PM_REPORT_STATUS_OPTIONS,
    type PMReportFrequencyFilter,
    type PMReportExcludableStatus
} from '../../services/reportService';
import { projectService } from '../../services/projectService';
import PageBreadcrumb from '../../shared/PageBreadCrumb';
import { useAuth } from '../../context/AuthContext';
import { format, differenceInDays } from 'date-fns';
import { resolveQuickPeriod, matchQuickPeriod, QUICK_PERIODS, type QuickPeriod } from './lib/quickDateRange';
import {
    loadLastUsed,
    saveLastUsed,
    resolveDateRule,
    loadPresets,
    savePreset,
    updatePreset,
    deletePreset,
    type DateRule,
    type PMReportPreset,
} from '../../services/pmReportPresetService';
import { PresetsBar } from './components/PresetsBar';
import { SavePresetDialog } from './components/SavePresetDialog';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import { Calendar, FileDown, FileText, Eye, X, TrendingUp, CheckCircle, XCircle, AlertCircle, Clock, Filter, History, SlidersHorizontal, ChevronDown, Info, Search } from 'lucide-react';

interface Project {
    id: string;
    projectId: string;
    projectName: string;
    location?: string;
    locationId?: {
        name: string;
        _id: string;
    };
}

interface ProjectsByLocation {
    [location: string]: Project[];
}

/** Single project in a history entry (for download again) */
interface HistoryProject {
    projectId: string;
    projectName: string;
}

/** One batch of generated Excel files (one "Generate" action) */
interface PMReportHistoryEntry {
    id: string;
    generatedAt: string;
    generatedBy: string;
    generatedByEmail?: string;
    dateRange: { startDate: string; endDate: string };
    /** Omitted or `all` = no frequency filter (legacy entries) */
    frequency?: PMReportFrequencyFilter;
    /** Statuses excluded from the report (legacy entries may omit). */
    excludeStatuses?: PMReportExcludableStatus[];
    projects: HistoryProject[];
    recordCount?: number;
}

const PM_REPORT_HISTORY_KEY = 'pm-report-history';
const PM_REPORT_HISTORY_MAX = 50;

/**
 * Preventive Maintenance Report Generator
 * Redesigned with "Prime" aesthetic and Project-First flow
 */
const PreventiveMaintenanceReport: React.FC = () => {
    const navigate = useNavigate();
    const { user } = useAuth();

    // Form state
    const [selectedProjectIds, setSelectedProjectIds] = useState<string[]>([]);
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [reportFrequency, setReportFrequency] = useState<PMReportFrequencyFilter>('all');
    // Advanced filter: task statuses to EXCLUDE from the report
    const [excludeStatuses, setExcludeStatuses] = useState<PMReportExcludableStatus[]>([]);
    const [showAdvanced, setShowAdvanced] = useState(false);

    const markModified = () => {
        if (activePresetId !== null) setPresetIsModified(true);
    };

    const toggleExcludeStatus = (value: PMReportExcludableStatus) => {
        setExcludeStatuses(prev =>
            prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]
        );
        markModified();
    };

    // UI state
    const [projects, setProjects] = useState<Project[]>([]);
    const [loading, setLoading] = useState(false);
    const [projectsLoading, setProjectsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [restoredFromLastUsed, setRestoredFromLastUsed] = useState(false);

    // Project filtering state
    const [projectSearch, setProjectSearch] = useState('');
    const [showClosedProjects, setShowClosedProjects] = useState(false); // Default hidden

    // New UI state for redesigned shell
    const [showDateMenu, setShowDateMenu] = useState(false);
    const [showActivity, setShowActivity] = useState(false);

    // Generation progress
    const [generationProgress, setGenerationProgress] = useState<{ current: number; total: number; projectName: string } | null>(null);

    // Report generation history (persisted in localStorage)
    const [reportHistory, setReportHistory] = useState<PMReportHistoryEntry[]>(() => {
        try {
            const raw = localStorage.getItem(PM_REPORT_HISTORY_KEY);
            if (!raw) return [];
            const parsed = JSON.parse(raw) as PMReportHistoryEntry[];
            return Array.isArray(parsed) ? parsed.slice(0, PM_REPORT_HISTORY_MAX) : [];
        } catch {
            return [];
        }
    });

    // Presets state
    const [presets, setPresets] = useState<PMReportPreset[]>(() => loadPresets());
    const [activePresetId, setActivePresetId] = useState<string | null | 'last-used'>(null);
    const [presetIsModified, setPresetIsModified] = useState(false);
    const [showSaveDialog, setShowSaveDialog] = useState(false);

    // Get today's date for max date validation
    const today = new Date().toISOString().split('T')[0];

    // Filter and categorize projects
    const { activeProjects, closedProjects } = useMemo(() => {
        const now = new Date();
        now.setHours(0, 0, 0, 0);

        const active: Project[] = [];
        const closed: Project[] = [];

        projects.forEach(project => {
            const projectData = project as any;
            const approvalStatus = (projectData.approvalStatus || '').toLowerCase();
            const projectStatus = (projectData.status || projectData.projectStatus || '').toLowerCase();

            // Check if project is closed/completed
            const isClosed = projectStatus === 'closed' || projectStatus === 'completed' || projectStatus === 'finished';

            if (isClosed) {
                closed.push(project);
            } else {
                // Check if project is active (approved and started)
                const isApproved = approvalStatus === 'approved' || approvalStatus === 'not_required';

                if (isApproved) {
                    // Check start date
                    const startDateStr = projectData.startDate || projectData.actualStartDate;
                    if (startDateStr) {
                        const startDate = new Date(startDateStr);
                        startDate.setHours(0, 0, 0, 0);
                        // Only include if started (date is today or in the past)
                        if (startDate.getTime() <= now.getTime()) {
                            active.push(project);
                        }
                    } else {
                        // No start date but approved - consider as active
                        active.push(project);
                    }
                }
            }
        });

        return { activeProjects: active, closedProjects: closed };
    }, [projects]);

    // Filter projects based on search and closed toggle
    const filteredProjects = useMemo(() => {
        let projectsToShow = [...activeProjects];

        // Add closed projects if toggle is on
        if (showClosedProjects) {
            projectsToShow = [...projectsToShow, ...closedProjects];
        }

        // Apply search filter
        if (projectSearch.trim()) {
            const searchLower = projectSearch.toLowerCase();
            projectsToShow = projectsToShow.filter(project =>
                project.projectName?.toLowerCase().includes(searchLower) ||
                project.projectId?.toLowerCase().includes(searchLower) ||
                project.location?.toLowerCase().includes(searchLower) ||
                project.locationId?.name?.toLowerCase().includes(searchLower)
            );
        }

        return projectsToShow;
    }, [activeProjects, closedProjects, showClosedProjects, projectSearch]);

    // Group filtered projects by location
    const projectsByLocation = useMemo<ProjectsByLocation>(() => {
        const grouped: ProjectsByLocation = {};
        filteredProjects.forEach(project => {
            const locationName = project.locationId?.name || project.location || 'Other Locations';
            if (!grouped[locationName]) {
                grouped[locationName] = [];
            }
            grouped[locationName].push(project);
        });
        return Object.keys(grouped).sort().reduce((acc, key) => {
            acc[key] = grouped[key];
            return acc;
        }, {} as ProjectsByLocation);
    }, [filteredProjects]);

    // Helper: count selected projects in a given location group
    const selectedInGroup = useCallback((location: string): number => {
        const list = projectsByLocation[location] || [];
        return list.reduce((acc, p) => acc + (selectedProjectIds.includes(p.id) ? 1 : 0), 0);
    }, [projectsByLocation, selectedProjectIds]);

    // Derived: number of closed projects available to show
    const closedCount = closedProjects.length;

    // Load projects on mount
    useEffect(() => {
        fetchProjects();
    }, []);

    // Restore last-used config after projects load
    useEffect(() => {
        if (projectsLoading) return;
        if (selectedProjectIds.length > 0 || startDate || endDate) return; // user already touched form
        const snap = loadLastUsed();
        if (!snap) return;

        const availableSet = new Set(projects.map(p => p.id));
        const keptIds = snap.projectIds.filter(id => availableSet.has(id));
        if (keptIds.length === 0) return;

        setSelectedProjectIds(keptIds);
        setReportFrequency(snap.frequency);
        setExcludeStatuses(snap.excludeStatuses);
        const { startDate: s, endDate: e } = resolveDateRule(snap.dateRule, new Date());
        setStartDate(s);
        setEndDate(e);
        setRestoredFromLastUsed(true);
        setActivePresetId('last-used');
    }, [projectsLoading, projects]);

    // Persist report history to localStorage
    useEffect(() => {
        try {
            localStorage.setItem(PM_REPORT_HISTORY_KEY, JSON.stringify(reportHistory));
        } catch {
            // ignore quota or parse errors
        }
    }, [reportHistory]);

    const fetchProjects = async () => {
        try {
            setProjectsLoading(true);
            const response = await projectService.getProjects({ limit: 1000 });
            setProjects(response.data || []);
        } catch (err: any) {
            console.error('Failed to load projects:', err);
            setError('Failed to load projects. Please refresh the page.');
        } finally {
            setProjectsLoading(false);
        }
    };


    // Quick select handlers
    const handleQuickSelect = (period: QuickPeriod) => {
        const { startDate: s, endDate: e } = resolveQuickPeriod(period, new Date());
        setStartDate(s);
        setEndDate(e);
        markModified();
        setError(null);
        setSuccess(null);
    };

    const activeQuickPreset = useMemo<QuickPeriod | null>(
        () => matchQuickPeriod(startDate, endDate, new Date()),
        [startDate, endDate]
    );

    const activeOutputFormat: 'excel' | 'pdf' = (() => {
        if (activePresetId && activePresetId !== 'last-used') {
            return presets.find(p => p.id === activePresetId)?.outputFormat ?? 'excel';
        }
        if (activePresetId === 'last-used') {
            return loadLastUsed()?.outputFormat ?? 'excel';
        }
        return 'excel';
    })();

    // Project selection handlers
    const handleProjectToggle = (projectId: string) => {
        setSelectedProjectIds(prev =>
            prev.includes(projectId)
                ? prev.filter(id => id !== projectId)
                : [...prev, projectId]
        );
        markModified();
        setError(null);
    };

    const handleSelectAllLocation = (location: string) => {
        const locationProjectIds = projectsByLocation[location].map(p => p.id);
        const allSelected = locationProjectIds.every(id => selectedProjectIds.includes(id));

        if (allSelected) {
            setSelectedProjectIds(prev => prev.filter(id => !locationProjectIds.includes(id)));
        } else {
            setSelectedProjectIds(prev => [...new Set([...prev, ...locationProjectIds])]);
        }
        markModified();
    };

    const handleClearAll = () => {
        setSelectedProjectIds([]);
        markModified();
    };

    // Preset config application helper
    const applyConfig = (cfg: {
        projectIds: string[];
        frequency: typeof reportFrequency;
        excludeStatuses: typeof excludeStatuses;
        outputFormat: 'excel' | 'pdf';
        dateRule: DateRule;
    }) => {
        const availableSet = new Set(projects.map(p => p.id));
        const keptIds = cfg.projectIds.filter(id => availableSet.has(id));
        const missingCount = cfg.projectIds.length - keptIds.length;

        setSelectedProjectIds(keptIds);
        setReportFrequency(cfg.frequency);
        setExcludeStatuses(cfg.excludeStatuses);
        const { startDate: s, endDate: e } = resolveDateRule(cfg.dateRule, new Date());
        setStartDate(s);
        setEndDate(e);
        setPresetIsModified(false);

        if (missingCount > 0 && keptIds.length > 0) {
            setSuccess(`${missingCount} project${missingCount === 1 ? '' : 's'} in this preset are no longer available — they'll be skipped.`);
        }
        return { allMissing: cfg.projectIds.length > 0 && keptIds.length === 0 };
    };

    const handleSelectPreset = (id: string) => {
        const preset = presets.find(p => p.id === id);
        if (!preset) return;
        const result = applyConfig(preset);
        if (result.allMissing) {
            setError(`None of "${preset.name}"'s projects are available anymore. Edit or delete the preset to continue.`);
            setActivePresetId(null);
            return;
        }
        setActivePresetId(preset.id);
        setRestoredFromLastUsed(false);
    };

    const handleSelectLastUsed = () => {
        const snap = loadLastUsed();
        if (!snap) return;
        const result = applyConfig(snap);
        if (result.allMissing) {
            setError('None of your last report’s projects are available anymore.');
            setActivePresetId(null);
            return;
        }
        setActivePresetId('last-used');
        setRestoredFromLastUsed(false);
    };

    // Save dialog handlers
    const canSavePreset =
        selectedProjectIds.length > 0 && !!startDate && !!endDate;

    const handleOpenSaveDialog = () => setShowSaveDialog(true);
    const handleCancelSaveDialog = () => setShowSaveDialog(false);

    const handleSaveNewPreset = ({ name, dateRule }: { name: string; dateRule: DateRule }) => {
        try {
            const created = savePreset({
                name,
                projectIds: [...selectedProjectIds],
                frequency: reportFrequency,
                excludeStatuses,
                outputFormat: 'excel',
                dateRule,
            });
            setPresets(loadPresets());
            setActivePresetId(created.id);
            setPresetIsModified(false);
            setShowSaveDialog(false);
            setSuccess(`Saved preset "${created.name}".`);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Could not save preset.');
        }
    };

    const handleUpdateActive = () => {
        if (!activePresetId || activePresetId === 'last-used') return;
        try {
            updatePreset(activePresetId, {
                projectIds: [...selectedProjectIds],
                frequency: reportFrequency,
                excludeStatuses,
                dateRule: activeQuickPreset
                    ? { kind: 'rolling', preset: activeQuickPreset }
                    : { kind: 'absolute', startDate, endDate },
            });
            setPresets(loadPresets());
            setPresetIsModified(false);
            setSuccess('Preset updated.');
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Could not update preset.');
        }
    };

    const handleRenamePreset = (id: string) => {
        const preset = presets.find(p => p.id === id);
        if (!preset) return;
        const next = window.prompt('Rename preset', preset.name);
        if (next === null) return;
        try {
            const trimmed = next.trim();
            updatePreset(id, { name: trimmed });
            setPresets(loadPresets());
            setSuccess(`Renamed to "${trimmed}".`);
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : 'Could not rename preset.');
        }
    };

    const handleDeletePreset = (id: string) => {
        const preset = presets.find(p => p.id === id);
        if (!preset) return;
        if (!window.confirm(`Delete preset "${preset.name}"?`)) return;
        deletePreset(id);
        setPresets(loadPresets());
        if (activePresetId === id) setActivePresetId(null);
    };

    // Validate form
    const validateForm = (): boolean => {
        if (selectedProjectIds.length === 0) {
            setError('Please select at least one project');
            return false;
        }
        if (!startDate) {
            setError('Please select a start date');
            return false;
        }
        if (!endDate) {
            setError('Please select an end date');
            return false;
        }
        if (new Date(startDate) > new Date(endDate)) {
            setError('Start date cannot be after end date');
            return false;
        }
        if (new Date(startDate) > new Date(today) || new Date(endDate) > new Date(today)) {
            setError('Cannot select future dates');
            return false;
        }
        return true;
    };

    // Preview handler - Navigate to preview page
    const handlePreviewReport = () => {
        setError(null);
        setSuccess(null);
        if (!validateForm()) return;

        navigate('/reports/preventive-maintenance/preview', {
            state: {
                selectedProjectIds,
                startDate,
                endDate,
                frequency: reportFrequency,
                excludeStatuses,
                projects
            }
        });
    };


    const handleGenerateReportPdf = async () => {
        setError(null);
        setSuccess(null);
        if (!validateForm()) return;

        try {
            setLoading(true);
            const total = selectedProjectIds.length;
            let successCount = 0;
            const historyProjects: HistoryProject[] = [];
            let totalRecordCount = 0;

            for (let i = 0; i < selectedProjectIds.length; i++) {
                const projectId = selectedProjectIds[i];
                const project = projects.find(p => p.id === projectId);

                setGenerationProgress({
                    current: i + 1,
                    total,
                    projectName: `${project?.projectName || 'Unknown'} (PDF)`
                });

                try {
                    const request: IReportRequest = {
                        projectId,
                        startDate: new Date(startDate).toISOString(),
                        endDate: new Date(endDate).toISOString(),
                        ...(reportFrequency !== 'all' ? { frequency: reportFrequency } : {}),
                        ...(excludeStatuses.length > 0 ? { excludeStatuses } : {})
                    };

                    const result = await reportService.generatePreventiveMaintenanceReportPdf(request);
                    const projectName = project?.projectName || project?.projectId || 'Unknown';
                    reportService.downloadPdfFile(result.blob, projectName, startDate, endDate);
                    successCount++;
                    historyProjects.push({ projectId, projectName });
                    if (typeof result.recordCount === 'number') {
                        totalRecordCount += result.recordCount;
                    }
                } catch (err) {
                    console.error(`Failed to generate PDF for ${project?.projectName}:`, err);
                }
            }

            setGenerationProgress(null);

            if (successCount > 0) {
                const entry: PMReportHistoryEntry = {
                    id: crypto.randomUUID(),
                    generatedAt: new Date().toISOString(),
                    generatedBy: user?.name ?? user?.email ?? 'Unknown',
                    generatedByEmail: user?.email,
                    dateRange: { startDate, endDate },
                    ...(reportFrequency !== 'all' ? { frequency: reportFrequency } : {}),
                    ...(excludeStatuses.length > 0 ? { excludeStatuses } : {}),
                    projects: historyProjects,
                    recordCount: totalRecordCount > 0 ? totalRecordCount : undefined
                };
                setReportHistory(prev => [entry, ...prev].slice(0, PM_REPORT_HISTORY_MAX));
                const dateRule: DateRule = activeQuickPreset
                    ? { kind: 'rolling', preset: activeQuickPreset }
                    : { kind: 'absolute', startDate, endDate };
                try {
                    saveLastUsed({
                        projectIds: [...selectedProjectIds],
                        frequency: reportFrequency,
                        excludeStatuses,
                        outputFormat: 'pdf',
                        dateRule,
                    });
                } catch { /* non-fatal */ }
            }

            if (successCount === total) {
                setSuccess(`Successfully generated ${successCount} PDF report(s)!`);
            } else {
                setSuccess(`Generated ${successCount} of ${total} PDF reports. Some failed.`);
            }
        } catch (err: unknown) {
            console.error('PDF report generation failed:', err);
            setError(err instanceof Error ? err.message : 'Failed to generate PDF reports. Please try again.');
        } finally {
            setLoading(false);
            setGenerationProgress(null);
        }
    };

    // Generate handler
    const handleGenerateReport = async () => {
        setError(null);
        setSuccess(null);
        if (!validateForm()) return;

        try {
            setLoading(true);
            const total = selectedProjectIds.length;
            let successCount = 0;
            const historyProjects: HistoryProject[] = [];
            let totalRecordCount = 0;

            for (let i = 0; i < selectedProjectIds.length; i++) {
                const projectId = selectedProjectIds[i];
                const project = projects.find(p => p.id === projectId);

                setGenerationProgress({
                    current: i + 1,
                    total,
                    projectName: project?.projectName || 'Unknown'
                });

                try {
                    const request: IReportRequest = {
                        projectId,
                        startDate: new Date(startDate).toISOString(),
                        endDate: new Date(endDate).toISOString(),
                        ...(reportFrequency !== 'all' ? { frequency: reportFrequency } : {}),
                        ...(excludeStatuses.length > 0 ? { excludeStatuses } : {})
                    };

                    const result = await reportService.generatePreventiveMaintenanceReport(request);
                    const projectName = project?.projectName || project?.projectId || 'Unknown';
                    reportService.downloadExcelFile(result.blob, projectName, startDate, endDate);
                    successCount++;
                    historyProjects.push({ projectId, projectName });
                    if (typeof result.recordCount === 'number') {
                        totalRecordCount += result.recordCount;
                    }
                } catch (err) {
                    console.error(`Failed to generate report for ${project?.projectName}:`, err);
                }
            }

            setGenerationProgress(null);

            if (successCount > 0) {
                const entry: PMReportHistoryEntry = {
                    id: crypto.randomUUID(),
                    generatedAt: new Date().toISOString(),
                    generatedBy: user?.name ?? user?.email ?? 'Unknown',
                    generatedByEmail: user?.email,
                    dateRange: { startDate, endDate },
                    ...(reportFrequency !== 'all' ? { frequency: reportFrequency } : {}),
                    ...(excludeStatuses.length > 0 ? { excludeStatuses } : {}),
                    projects: historyProjects,
                    recordCount: totalRecordCount > 0 ? totalRecordCount : undefined
                };
                setReportHistory(prev => [entry, ...prev].slice(0, PM_REPORT_HISTORY_MAX));
                const dateRule: DateRule = activeQuickPreset
                    ? { kind: 'rolling', preset: activeQuickPreset }
                    : { kind: 'absolute', startDate, endDate };
                try {
                    saveLastUsed({
                        projectIds: [...selectedProjectIds],
                        frequency: reportFrequency,
                        excludeStatuses,
                        outputFormat: 'excel',
                        dateRule,
                    });
                } catch {
                    // localStorage write failed — non-fatal
                }
            }

            if (successCount === total) {
                setSuccess(`Successfully generated ${successCount} report(s)!`);
            } else {
                setSuccess(`Generated ${successCount} of ${total} reports. Some reports failed.`);
            }
        } catch (err: unknown) {
            console.error('Report generation failed:', err);
            setError(err instanceof Error ? err.message : 'Failed to generate reports. Please try again.');
        } finally {
            setLoading(false);
            setGenerationProgress(null);
        }
    };

    // Download again: re-generate and download for the same filters as a history entry
    const handleDownloadAgain = useCallback(async (entry: PMReportHistoryEntry) => {
        setError(null);
        setSuccess(null);
        try {
            setLoading(true);
            for (let i = 0; i < entry.projects.length; i++) {
                const { projectId, projectName } = entry.projects[i];
                const request: IReportRequest = {
                    projectId,
                    startDate: new Date(entry.dateRange.startDate).toISOString(),
                    endDate: new Date(entry.dateRange.endDate).toISOString(),
                    ...(entry.frequency && entry.frequency !== 'all' ? { frequency: entry.frequency } : {}),
                    ...(entry.excludeStatuses && entry.excludeStatuses.length > 0
                        ? { excludeStatuses: entry.excludeStatuses }
                        : {})
                };
                const result = await reportService.generatePreventiveMaintenanceReport(request);
                reportService.downloadExcelFile(
                    result.blob,
                    projectName,
                    entry.dateRange.startDate,
                    entry.dateRange.endDate
                );
            }
            setSuccess(`Downloaded ${entry.projects.length} report(s) again.`);
        } catch (err: unknown) {
            console.error('Download again failed:', err);
            setError(err instanceof Error ? err.message : 'Failed to download. Please try again.');
        } finally {
            setLoading(false);
        }
    }, []);

    // Keyboard shortcuts (wired in parallel hook)
    useKeyboardShortcuts({
        onGenerateExcel: () => handleGenerateReport(),
        onGeneratePdf: () => handleGenerateReportPdf(),
        onPreview: () => handlePreviewReport(),
        onFocusSearch: () => document.getElementById('pm-search')?.focus(),
    });

    // Derived UI values
    const noProjectsSelected = selectedProjectIds.length === 0;
    const noDatesSet = !startDate || !endDate;
    const totalDays = startDate && endDate
        ? differenceInDays(new Date(endDate), new Date(startDate)) + 1
        : 0;
    const visibleProjectCount = filteredProjects.length;
    const activeQuickPresetLabel = activeQuickPreset
        ? (QUICK_PERIODS.find(p => p.key === activeQuickPreset)?.label ?? 'Custom')
        : 'Custom';

    const previewDisabledReason = noProjectsSelected
        ? 'Select at least one project'
        : noDatesSet
            ? 'Choose a date range'
            : 'Preview the report in the browser';
    const generateDisabledReason = noProjectsSelected
        ? 'Select at least one project'
        : noDatesSet
            ? 'Choose a date range'
            : '';

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-slate-900 pb-12">
            <PageBreadcrumb pageTitle="Preventive Maintenance Report" />

            {/* Sticky top header with action cluster */}
            <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur dark:border-slate-700 dark:bg-slate-900/95">
                <div
                    className="absolute left-0 top-0 bottom-0 w-1.5"
                    style={{
                        background: 'linear-gradient(to right, #92D050 0%, #059669 45%, rgba(255,255,255,0.95) 100%)',
                    }}
                    aria-hidden
                />
                <div className="relative mx-auto max-w-[1600px] px-4 py-3 sm:px-6 flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <h1 className="text-lg font-semibold tracking-tight text-slate-900 dark:text-white">
                            Preventive Maintenance Report
                        </h1>
                        <p className="text-xs text-slate-500 dark:text-slate-400">
                            Select projects, date range, and optional maintenance frequency
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center justify-end gap-2">
                        {/* Selection chip */}
                        {selectedProjectIds.length > 0 && !noDatesSet ? (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-3 py-1 text-xs font-medium text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300">
                                {selectedProjectIds.length} project{selectedProjectIds.length === 1 ? '' : 's'} · {totalDays} day{totalDays === 1 ? '' : 's'}
                            </span>
                        ) : (
                            <span className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-500 dark:bg-slate-700/50 dark:text-slate-400">
                                {noProjectsSelected ? 'No projects selected' : `${selectedProjectIds.length} project${selectedProjectIds.length === 1 ? '' : 's'} · pick dates`}
                            </span>
                        )}

                        {/* Preview button */}
                        <button
                            type="button"
                            onClick={handlePreviewReport}
                            disabled={noProjectsSelected || noDatesSet}
                            title={previewDisabledReason}
                            className="inline-flex items-center gap-1.5 rounded-md px-3 h-9 text-sm font-medium text-slate-700 hover:bg-slate-100 disabled:opacity-50 disabled:cursor-not-allowed dark:text-slate-200 dark:hover:bg-slate-700"
                        >
                            <Eye className="h-4 w-4" />
                            Preview
                        </button>

                        {/* Generate PDF — outline */}
                        <button
                            type="button"
                            onClick={handleGenerateReportPdf}
                            disabled={loading || noProjectsSelected || noDatesSet}
                            title={generateDisabledReason || 'Generate PDF report(s)'}
                            className={`inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 h-9 text-sm font-semibold text-slate-800 hover:border-emerald-400 hover:text-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-emerald-500 dark:hover:bg-slate-700 ${activeOutputFormat === 'pdf' ? 'ring-2 ring-rose-200 dark:ring-rose-900/40' : ''}`}
                        >
                            <FileText className="h-4 w-4" />
                            Generate PDF
                        </button>

                        {/* Generate Excel — primary */}
                        <button
                            type="button"
                            onClick={handleGenerateReport}
                            disabled={loading || noProjectsSelected || noDatesSet}
                            title={generateDisabledReason || 'Generate Excel report(s)'}
                            className="inline-flex items-center gap-1.5 rounded-md bg-[#006346] px-4 h-9 text-sm font-semibold text-white shadow-sm shadow-emerald-900/30 transition hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed dark:focus-visible:ring-offset-slate-900"
                        >
                            {loading ? (
                                <>
                                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                                    Generating…
                                </>
                            ) : (
                                <>
                                    <FileDown className="h-4 w-4" />
                                    Generate Excel
                                </>
                            )}
                        </button>
                    </div>
                </div>
            </header>

            {/* Presets bar – kept exactly where it is */}
            <div className="relative">
                <PresetsBar
                    presets={presets}
                    hasLastUsed={loadLastUsed() !== null}
                    activeId={activePresetId}
                    isModified={presetIsModified}
                    canSaveNew={canSavePreset}
                    onSelectPreset={handleSelectPreset}
                    onSelectLastUsed={handleSelectLastUsed}
                    onOpenSaveDialog={handleOpenSaveDialog}
                    onUpdateActive={handleUpdateActive}
                    onRename={handleRenamePreset}
                    onDelete={handleDeletePreset}
                />
                {showSaveDialog && (
                    <div className="absolute right-3 top-full z-20 mt-1 w-80">
                        <SavePresetDialog
                            open
                            onSave={handleSaveNewPreset}
                            onCancel={handleCancelSaveDialog}
                            existingNames={presets.map(p => p.name)}
                            activeQuickPreset={activeQuickPreset}
                            absoluteRange={{ startDate, endDate }}
                        />
                    </div>
                )}
            </div>

            {restoredFromLastUsed && (
                <div role="status" className="mx-auto mt-2 flex max-w-[1600px] items-center justify-between gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs dark:border-emerald-800 dark:bg-emerald-900/20">
                    <span className="text-emerald-800 dark:text-emerald-200">Restored your last report.</span>
                    <button
                        type="button"
                        onClick={() => {
                            setSelectedProjectIds([]);
                            setStartDate('');
                            setEndDate('');
                            setReportFrequency('all');
                            setExcludeStatuses([]);
                            setRestoredFromLastUsed(false);
                            setActivePresetId(null);
                            setPresetIsModified(false);
                        }}
                        className="text-emerald-700 underline hover:text-emerald-900 dark:text-emerald-300"
                    >
                        Clear
                    </button>
                </div>
            )}

            {/* Toast notifications */}
            {(error || success) && (
                <div className="fixed right-6 top-20 z-50 w-full max-w-sm space-y-2">
                    {error && (
                        <div className="rounded-lg border border-red-200 bg-white p-3 shadow-lg dark:border-red-800 dark:bg-slate-800">
                            <div className="flex items-start gap-2">
                                <XCircle className="h-5 w-5 shrink-0 text-red-500" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-slate-700 dark:text-slate-200">{error}</p>
                                </div>
                                <button onClick={() => setError(null)} className="text-slate-400 hover:text-slate-600">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    )}
                    {success && (
                        <div className="rounded-lg border border-emerald-200 bg-white p-3 shadow-lg dark:border-emerald-800 dark:bg-slate-800">
                            <div className="flex items-start gap-2">
                                <CheckCircle className="h-5 w-5 shrink-0 text-emerald-500" />
                                <div className="flex-1 min-w-0">
                                    <p className="text-sm text-slate-700 dark:text-slate-200">{success}</p>
                                </div>
                                <button onClick={() => setSuccess(null)} className="text-slate-400 hover:text-slate-600">
                                    <X className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Centered top progress toast */}
            {generationProgress && (
                <div className="fixed left-1/2 top-20 z-40 -translate-x-1/2 w-[min(640px,90vw)] rounded-xl border border-emerald-200 bg-white p-4 shadow-lg dark:border-emerald-700 dark:bg-slate-800">
                    <div className="flex items-center gap-3">
                        <div className="h-5 w-5 shrink-0 animate-spin rounded-full border-2 border-emerald-500/30 border-t-emerald-600" />
                        <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium text-slate-800 dark:text-slate-100">
                                Generating {generationProgress.current} of {generationProgress.total} — {generationProgress.projectName}
                            </p>
                        </div>
                        <span className="shrink-0 text-sm font-semibold text-emerald-600 tabular-nums">
                            {Math.round((generationProgress.current / generationProgress.total) * 100)}%
                        </span>
                    </div>
                    <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-emerald-100 dark:bg-emerald-900/40">
                        <div
                            className="h-full bg-emerald-600 transition-all duration-300"
                            style={{ width: `${(generationProgress.current / generationProgress.total) * 100}%` }}
                        />
                    </div>
                </div>
            )}

            {/* Body */}
            <div className="mx-auto max-w-[1600px] px-4 py-4 sm:px-6 space-y-4">
                <div className="grid gap-4 lg:grid-cols-[1fr_22rem]">

                    {/* LEFT: Projects panel */}
                    <div className="min-w-0">
                        <div className="rounded-xl border border-slate-200/70 bg-white shadow-sm dark:border-slate-700/60 dark:bg-slate-800">
                            {/* Toolbar */}
                            <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 px-4 py-3 dark:border-slate-700/60">
                                <div className="flex items-center gap-2">
                                    <Filter className="h-4 w-4 text-emerald-600" />
                                    <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Projects</h2>
                                    <span className="text-xs text-slate-500">
                                        {visibleProjectCount} visible · {selectedProjectIds.length} selected
                                    </span>
                                </div>

                                {/* Search */}
                                <div className="relative flex-1 min-w-[200px]">
                                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                                    <input
                                        id="pm-search"
                                        data-search-input
                                        type="text"
                                        value={projectSearch}
                                        onChange={(e) => setProjectSearch(e.target.value)}
                                        placeholder="Search projects..."
                                        className="w-full rounded-md border border-slate-200 bg-slate-50 py-2 pl-9 pr-10 text-sm placeholder:text-slate-400 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                                    />
                                    <kbd className="absolute right-3 top-1/2 -translate-y-1/2 rounded border border-slate-200 px-1.5 py-0.5 text-[10px] font-mono text-slate-400 dark:border-slate-600">/</kbd>
                                </div>

                                {/* Show closed toggle */}
                                <label className="inline-flex items-center gap-2 cursor-pointer text-xs font-medium text-slate-600 dark:text-slate-300">
                                    <input
                                        type="checkbox"
                                        checked={showClosedProjects}
                                        onChange={(e) => setShowClosedProjects(e.target.checked)}
                                        className="accent-emerald-600"
                                    />
                                    Show closed ({closedCount})
                                </label>

                                {/* Clear all */}
                                {selectedProjectIds.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={handleClearAll}
                                        className="text-xs font-medium text-rose-600 hover:bg-rose-50 rounded-md px-2 py-1 dark:hover:bg-rose-900/20"
                                    >
                                        Clear ({selectedProjectIds.length})
                                    </button>
                                )}
                            </div>

                            {/* Scroll body */}
                            <div className="max-h-[calc(100vh-18rem)] overflow-y-auto p-3 space-y-5">
                                {projectsLoading ? (
                                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                                        {Array.from({ length: 6 }).map((_, i) => (
                                            <div key={i} className="h-16 rounded-lg bg-slate-100 dark:bg-slate-700/40 animate-pulse" />
                                        ))}
                                    </div>
                                ) : Object.keys(projectsByLocation).length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-12 text-center">
                                        <AlertCircle className="h-10 w-10 text-slate-300" />
                                        <p className="mt-2 text-sm font-medium text-slate-600 dark:text-slate-400">No projects match your filter</p>
                                        {projectSearch && (
                                            <button
                                                type="button"
                                                onClick={() => setProjectSearch('')}
                                                className="mt-3 inline-flex items-center gap-1.5 rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
                                            >
                                                Clear search
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    Object.entries(projectsByLocation).map(([location, locProjects]) => {
                                        const locSelected = selectedInGroup(location);
                                        const locTotal = locProjects.length;
                                        const allSelected = locProjects.every(p => selectedProjectIds.includes(p.id));
                                        return (
                                            <div key={location}>
                                                <div className="sticky top-0 z-10 -mx-3 -mt-3 mb-2 flex items-center justify-between bg-white/95 backdrop-blur px-3 py-2 dark:bg-slate-800/95 border-b border-slate-100 dark:border-slate-700/60">
                                                    <div className="flex items-center gap-2 min-w-0">
                                                        <span className="text-xs font-semibold text-slate-700 dark:text-slate-200 truncate">{location}</span>
                                                        <span className="text-[11px] text-slate-500">({locSelected}/{locTotal})</span>
                                                    </div>
                                                    <button
                                                        type="button"
                                                        onClick={() => handleSelectAllLocation(location)}
                                                        className="text-[11px] font-semibold text-emerald-600 hover:underline"
                                                    >
                                                        {allSelected ? `Clear (${locTotal})` : `Select all (${locTotal})`}
                                                    </button>
                                                </div>

                                                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                                                    {locProjects.map(project => {
                                                        const isSelected = selectedProjectIds.includes(project.id);
                                                        const locText = project.locationId?.name || project.location || '';
                                                        return (
                                                            <div
                                                                key={project.id}
                                                                onClick={() => handleProjectToggle(project.id)}
                                                                className={`group cursor-pointer rounded-lg border p-3 transition ${
                                                                    isSelected
                                                                        ? 'border-emerald-500 bg-emerald-50/80 ring-1 ring-emerald-500/30 dark:border-emerald-400 dark:bg-emerald-500/10'
                                                                        : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800/40 dark:hover:bg-slate-700/40'
                                                                }`}
                                                            >
                                                                <div className="flex items-start gap-2">
                                                                    <div className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded border ${
                                                                        isSelected ? 'border-emerald-600 bg-emerald-600' : 'border-slate-300 dark:border-slate-500'
                                                                    }`}>
                                                                        {isSelected && <CheckCircle className="h-2.5 w-2.5 text-white" strokeWidth={3} />}
                                                                    </div>
                                                                    <div className="min-w-0 flex-1">
                                                                        <h4 className={`truncate text-sm font-semibold leading-tight ${
                                                                            isSelected ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-800 dark:text-slate-100'
                                                                        }`}>
                                                                            {project.projectName}
                                                                        </h4>
                                                                        <p className="mt-0.5 truncate text-[11px] text-slate-500 dark:text-slate-400">
                                                                            {project.projectId}{locText ? ` · ${locText}` : ''}
                                                                        </p>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    })
                                )}
                            </div>
                        </div>

                        {/* Activity log accordion */}
                        <div className="mt-4 rounded-xl border border-slate-200/70 bg-white shadow-sm dark:border-slate-700/60 dark:bg-slate-800">
                            <button
                                type="button"
                                onClick={() => setShowActivity(v => !v)}
                                aria-expanded={showActivity}
                                className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700/40"
                            >
                                <div className="flex items-center gap-2">
                                    <History className="h-4 w-4 text-emerald-600" />
                                    <h2 className="text-sm font-semibold text-slate-900 dark:text-white">Activity log</h2>
                                    <span className="text-xs text-slate-500">
                                        {reportHistory.length} {reportHistory.length === 1 ? 'entry' : 'entries'}
                                    </span>
                                </div>
                                <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${showActivity ? 'rotate-180' : ''}`} />
                            </button>

                            {showActivity && (
                                <div className="max-h-80 overflow-y-auto">
                                    {reportHistory.length === 0 ? (
                                        <div className="flex flex-col items-center justify-center py-8 text-center border-t border-slate-100 dark:border-slate-700/60">
                                            <Clock className="h-10 w-10 text-slate-300 dark:text-slate-600" />
                                            <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">No activity yet</p>
                                            <p className="text-xs text-slate-400 dark:text-slate-500">Generated reports will be logged here for audit</p>
                                        </div>
                                    ) : (
                                        <table className="w-full text-xs">
                                            <thead className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400">
                                                <tr className="border-t border-slate-100 dark:border-slate-700/60">
                                                    <th className="px-4 py-2 text-left font-medium">When</th>
                                                    <th className="px-4 py-2 text-left font-medium">User</th>
                                                    <th className="px-4 py-2 text-left font-medium">Range</th>
                                                    <th className="px-4 py-2 text-left font-medium">Projects</th>
                                                    <th className="px-4 py-2 text-right font-medium">Records</th>
                                                    <th className="px-4 py-2 text-right font-medium">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-700/60">
                                                {reportHistory.map((entry) => {
                                                    const projectsLabel = entry.projects.length <= 3
                                                        ? entry.projects.map(p => p.projectName).join(', ')
                                                        : `${entry.projects.length} project${entry.projects.length !== 1 ? 's' : ''}`;
                                                    return (
                                                        <tr key={entry.id} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                                                            <td className="px-4 py-2 text-slate-600 dark:text-slate-300 tabular-nums whitespace-nowrap" title={entry.generatedAt}>
                                                                {format(new Date(entry.generatedAt), 'dd MMM yyyy, HH:mm')}
                                                            </td>
                                                            <td className="px-4 py-2 text-slate-700 dark:text-slate-200">
                                                                <div className="font-medium">{entry.generatedBy}</div>
                                                                {entry.generatedByEmail && entry.generatedBy !== entry.generatedByEmail && (
                                                                    <div className="text-[10px] text-slate-500">{entry.generatedByEmail}</div>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-2 text-slate-600 dark:text-slate-300 tabular-nums whitespace-nowrap">
                                                                {entry.dateRange.startDate} – {entry.dateRange.endDate}
                                                                {entry.frequency && entry.frequency !== 'all' && (
                                                                    <span className="ml-1 text-emerald-600 dark:text-emerald-400">
                                                                        · {PM_REPORT_FREQUENCY_OPTIONS.find((o) => o.value === entry.frequency)?.label ?? entry.frequency}
                                                                    </span>
                                                                )}
                                                                {entry.excludeStatuses && entry.excludeStatuses.length > 0 && (
                                                                    <span
                                                                        className="ml-1 text-amber-600 dark:text-amber-400"
                                                                        title={`Hidden: ${entry.excludeStatuses
                                                                            .map(s => PM_REPORT_STATUS_OPTIONS.find(o => o.value === s)?.label ?? s)
                                                                            .join(', ')}`}
                                                                    >
                                                                        · hides {entry.excludeStatuses.length}
                                                                    </span>
                                                                )}
                                                            </td>
                                                            <td className="px-4 py-2 text-slate-600 dark:text-slate-300 max-w-xs">
                                                                <span className="block truncate" title={entry.projects.map(p => p.projectName).join(', ')}>
                                                                    {projectsLabel}
                                                                </span>
                                                            </td>
                                                            <td className="px-4 py-2 text-right tabular-nums font-semibold text-emerald-600 dark:text-emerald-400">
                                                                {entry.recordCount != null ? entry.recordCount : '—'}
                                                            </td>
                                                            <td className="px-4 py-2 text-right">
                                                                <button
                                                                    type="button"
                                                                    onClick={() => handleDownloadAgain(entry)}
                                                                    disabled={loading}
                                                                    className="inline-flex items-center gap-1 rounded border border-slate-300 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300 dark:hover:bg-slate-700"
                                                                >
                                                                    <FileDown className="h-3.5 w-3.5" />
                                                                    Download
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* RIGHT: Configuration rail */}
                    <aside className="rounded-xl border border-slate-200/70 bg-white shadow-sm dark:border-slate-700/60 dark:bg-slate-800 lg:sticky lg:top-[5.5rem] self-start">
                        <div className="divide-y divide-slate-100 dark:divide-slate-700/60">

                            {/* Section A — Date range */}
                            <section>
                                <div className="px-4 pt-4 pb-2 flex items-center gap-2">
                                    <Calendar className="h-3.5 w-3.5 text-emerald-600" />
                                    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Date range</span>
                                </div>
                                <div className="px-4 pb-4 space-y-3">
                                    <button
                                        type="button"
                                        onClick={() => setShowDateMenu(v => !v)}
                                        aria-expanded={showDateMenu}
                                        className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm font-medium text-slate-700 hover:border-emerald-400 hover:bg-white dark:border-slate-600 dark:bg-slate-900 dark:text-slate-200"
                                    >
                                        <span className="inline-flex items-center gap-2">
                                            <span className="rounded-md bg-white px-2 py-0.5 text-xs font-semibold text-slate-700 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-600">
                                                {activeQuickPresetLabel}
                                            </span>
                                            {startDate && endDate && (
                                                <span className="text-xs text-slate-500 tabular-nums">
                                                    {startDate} → {endDate}
                                                </span>
                                            )}
                                        </span>
                                        <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${showDateMenu ? 'rotate-180' : ''}`} />
                                    </button>

                                    {showDateMenu && (
                                        <div className="mt-2 rounded-lg border border-slate-200 bg-white p-2 shadow-sm dark:border-slate-700 dark:bg-slate-900">
                                            <div className="flex flex-col">
                                                {QUICK_PERIODS.map(({ key, label }) => {
                                                    const active = activeQuickPreset === key;
                                                    return (
                                                        <button
                                                            key={key}
                                                            type="button"
                                                            onClick={() => { handleQuickSelect(key); setShowDateMenu(false); }}
                                                            className={`flex w-full items-center justify-between rounded-md px-3 py-1.5 text-sm text-left hover:bg-slate-100 dark:hover:bg-slate-700 ${
                                                                active ? 'bg-emerald-50 text-emerald-700 font-semibold dark:bg-emerald-500/15 dark:text-emerald-300' : 'text-slate-700 dark:text-slate-200'
                                                            }`}
                                                        >
                                                            <span>{label}</span>
                                                        </button>
                                                    );
                                                })}
                                                <button
                                                    type="button"
                                                    onClick={() => setShowDateMenu(false)}
                                                    className="flex w-full items-center justify-between rounded-md px-3 py-1.5 text-sm text-left text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
                                                >
                                                    <span>Custom</span>
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    <div className="space-y-2">
                                        <div>
                                            <label className="text-[11px] font-medium text-slate-500">Start</label>
                                            <input
                                                type="date"
                                                value={startDate}
                                                max={endDate && endDate < today ? endDate : today}
                                                onChange={(e) => {
                                                    let v = e.target.value;
                                                    if (v && v > today) v = today;
                                                    if (v && endDate && v > endDate) v = endDate;
                                                    setStartDate(v);
                                                    markModified();
                                                }}
                                                className="mt-1 w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[11px] font-medium text-slate-500">End</label>
                                            <input
                                                type="date"
                                                value={endDate}
                                                min={startDate || undefined}
                                                max={today}
                                                onChange={(e) => {
                                                    let v = e.target.value;
                                                    if (v && v > today) v = today;
                                                    if (v && startDate && v < startDate) v = startDate;
                                                    setEndDate(v);
                                                    markModified();
                                                }}
                                                className="mt-1 w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                                            />
                                        </div>
                                        <p className="text-[10px] text-slate-400 dark:text-slate-500">
                                            Future dates are disabled. Today is the latest selectable date.
                                        </p>
                                    </div>

                                    {startDate && endDate && (
                                        <div className="flex items-center gap-1.5 rounded-md bg-emerald-50 px-2 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300 w-fit">
                                            <TrendingUp className="h-3.5 w-3.5" />
                                            {totalDays} day{totalDays === 1 ? '' : 's'}
                                        </div>
                                    )}
                                </div>
                            </section>

                            {/* Section B — Frequency */}
                            <section>
                                <div className="px-4 pt-4 pb-2 flex items-center gap-2">
                                    <Filter className="h-3.5 w-3.5 text-emerald-600" />
                                    <span className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">Frequency</span>
                                </div>
                                <div className="px-4 pb-4">
                                    <div className="flex items-center gap-1.5">
                                        <span className="text-xs font-medium text-slate-600 dark:text-slate-400">Tasks to include</span>
                                        <Info
                                            className="h-3.5 w-3.5 text-slate-400"
                                            aria-label="Only tasks with this checkpoint frequency are included unless you choose Any frequency."
                                        >
                                            <title>Only tasks with this checkpoint frequency are included unless you choose Any frequency.</title>
                                        </Info>
                                    </div>
                                    <select
                                        value={reportFrequency}
                                        onChange={(e) => { setReportFrequency(e.target.value as PMReportFrequencyFilter); markModified(); }}
                                        className="mt-2 w-full rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20 dark:border-slate-600 dark:bg-slate-900 dark:text-white"
                                    >
                                        {PM_REPORT_FREQUENCY_OPTIONS.map((opt) => (
                                            <option key={opt.value} value={opt.value}>
                                                {opt.value === 'all' ? 'Any frequency' : opt.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </section>

                            {/* Section C — Advanced filters (collapsible) */}
                            <section>
                                <button
                                    type="button"
                                    onClick={() => setShowAdvanced(v => !v)}
                                    aria-expanded={showAdvanced}
                                    className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left hover:bg-slate-50 dark:hover:bg-slate-700/40"
                                >
                                    <div className="flex items-center gap-2">
                                        <SlidersHorizontal className="h-4 w-4 text-emerald-600" />
                                        <span className="text-sm font-semibold text-slate-900 dark:text-white">Filters</span>
                                        {excludeStatuses.length > 0 && (
                                            <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-700 dark:bg-amber-500/20 dark:text-amber-300">
                                                {excludeStatuses.length} hidden
                                            </span>
                                        )}
                                    </div>
                                    <ChevronDown className={`h-4 w-4 text-slate-400 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                                </button>

                                {showAdvanced && (
                                    <div className="px-4 pb-4">
                                        <div className="flex items-center justify-between mb-2">
                                            <label className="text-[11px] font-medium text-slate-500 uppercase tracking-wide">
                                                Hide task statuses
                                            </label>
                                            {excludeStatuses.length > 0 && (
                                                <button
                                                    type="button"
                                                    onClick={() => setExcludeStatuses([])}
                                                    className="shrink-0 rounded px-2 py-1 text-[11px] font-medium text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700"
                                                >
                                                    Reset
                                                </button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 gap-1.5">
                                            {PM_REPORT_STATUS_OPTIONS.map((opt) => {
                                                const checked = excludeStatuses.includes(opt.value);
                                                return (
                                                    <label
                                                        key={opt.value}
                                                        className={`flex cursor-pointer items-start gap-2 rounded-md border p-2 text-sm transition-colors ${
                                                            checked
                                                                ? 'border-emerald-500 bg-emerald-50/80 dark:border-emerald-400 dark:bg-emerald-500/10'
                                                                : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800/50 dark:hover:bg-slate-700/40'
                                                        }`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={checked}
                                                            onChange={() => toggleExcludeStatus(opt.value)}
                                                            className="mt-0.5 h-3.5 w-3.5 accent-emerald-600"
                                                        />
                                                        <div className="min-w-0 flex-1">
                                                            <p className={`text-sm font-semibold leading-tight ${checked ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-800 dark:text-slate-100'}`}>
                                                                {opt.label}
                                                            </p>
                                                            <p className="mt-0.5 text-[11px] text-slate-500 dark:text-slate-400">
                                                                {opt.description}
                                                            </p>
                                                        </div>
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </section>
                        </div>

                        {/* Section D — Footer hint */}
                        <div className="px-4 py-3 text-[11px] text-slate-500 dark:text-slate-400 flex items-center gap-2 border-t border-slate-100 dark:border-slate-700/60">
                            <span>Shortcuts:</span>
                            <kbd className="rounded border border-slate-300 px-1 font-mono text-[10px] dark:border-slate-600">/</kbd>
                            <span>search ·</span>
                            <kbd className="rounded border border-slate-300 px-1 font-mono text-[10px] dark:border-slate-600">Ctrl</kbd>
                            <span>+</span>
                            <kbd className="rounded border border-slate-300 px-1 font-mono text-[10px] dark:border-slate-600">↵</kbd>
                            <span>generate</span>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
};

export default PreventiveMaintenanceReport;
